/**
 * 데스크탑 캡처의 webview 측 상태 보관소.
 *
 * 캡처가 webview에 도착하면 R2 업로드를 시작하고 본 store에 추가된다.
 * UI(DesktopCaptureChip)는 본 store를 구독해 우하단 floating chip을 표시한다.
 *
 * 정책:
 * - 모든 캡처는 도착 즉시 활성 상태. "대기/큐" 상태는 두지 않는다.
 * - 최대 보관 수는 트리거 첨부 한도(`maxAttachmentsPerDaemonTrigger = 10`)와 동일.
 *   한도에 도달하면 새 캡처는 거부(addCapture가 false 반환)하고 사용자가 기존을 처리/폐기해야 한다.
 * - 칩의 신규/이어쓰기 액션은 `ready` 상태인 모든 캡처를 한 번에 첨부 후보로 전달한다.
 *
 * Persistence: 본 store는 in-memory. 페이지 새로고침 시 사라진다.
 */

import { create } from 'zustand';
import type { PendingAttachmentDraft } from '../types/attachment';

export const MAX_PENDING_CAPTURES = 10;

export type CaptureMode = 'new' | 'followUp';
export type CaptureStatus = 'uploading' | 'ready' | 'failed';

export type PendingCapture = {
  captureId: string;
  /** 데스크탑이 보낸 모드 힌트. 칩의 1차 액션 추천에만 사용. */
  mode: CaptureMode;
  status: CaptureStatus;
  description: string;
  rawOnly: boolean;
  width: number;
  height: number;
  capturedAt: string;
  /** 칩 thumbnail에 사용하는 dataURL (PNG base64). 업로드 후에도 UI 미리보기용으로 유지. */
  thumbnailDataUrl?: string;
  /** ready 상태에서만 채워진다. DaemonTriggerModal/follow-up picker로 주입. */
  attachmentDraft?: PendingAttachmentDraft;
  /** 실패 시 사용자에게 보여줄 메시지. */
  errorMessage?: string;
};

type DesktopCaptureState = {
  captures: PendingCapture[];
};

type DesktopCaptureActions = {
  /**
   * 새 캡처를 추가. 이미 한도에 도달했으면 false 반환 — 호출자는 사용자에게 거부 알림을 띄워야 한다.
   */
  addCapture(capture: PendingCapture): boolean;
  setStatus(captureId: string, status: CaptureStatus, patch?: Partial<Omit<PendingCapture, 'captureId'>>): void;
  /** 단일 캡처를 폐기(개별 ✕). */
  dismiss(captureId: string): void;
  /** 칩 전체 폐기 — 일괄 ✕. */
  dismissAll(): void;
  /** 일괄 첨부 후 store에서 ready 캡처들을 일괄 제거(소비). failed/uploading은 남긴다. */
  consumeReady(): PendingCapture[];
};

export const useDesktopCaptureStore = create<DesktopCaptureState & DesktopCaptureActions>((set, get) => ({
  captures: [],

  addCapture(capture) {
    const { captures } = get();
    if (captures.length >= MAX_PENDING_CAPTURES) return false;
    set({ captures: [...captures, capture] });
    return true;
  },

  setStatus(captureId, status, patch) {
    set((state) => ({
      captures: state.captures.map((c) => (c.captureId === captureId ? { ...c, status, ...(patch ?? {}) } : c)),
    }));
  },

  dismiss(captureId) {
    set((state) => ({
      captures: state.captures.filter((c) => c.captureId !== captureId),
    }));
  },

  dismissAll() {
    set({ captures: [] });
  },

  consumeReady() {
    const ready = get().captures.filter((c) => c.status === 'ready');
    set((state) => ({
      captures: state.captures.filter((c) => c.status !== 'ready'),
    }));
    return ready;
  },
}));

/** 칩이 활성화되는 캡처 수. */
export const selectActiveCount = (state: DesktopCaptureState): number => state.captures.length;

/** ready 상태인 캡처만. */
export const selectReady = (state: DesktopCaptureState): PendingCapture[] =>
  state.captures.filter((c) => c.status === 'ready');

/** 한도까지 남은 슬롯 수. */
export const selectRemainingSlots = (state: DesktopCaptureState): number =>
  Math.max(0, MAX_PENDING_CAPTURES - state.captures.length);
